import React, { useState } from 'react';

import { FileType } from '../../../../../types/common.types';
import { Comment, HashTag } from '../../../../../types/entity.types';

import { APPROVED, ARCHIVED, DECLINED, PENDING } from '../../../../../utils/constants';
import { formatUsername, getClassNames } from '../../../../../utils/util';

import ProfilePicture from '../../../../common/profile-picture/ProfilePicture';
import TribeApprovalButton from '../tribe-approval-button/TribeApprovalButton';
import TribeThumbnails from '../tribe-thumbnails/TribeThumbnails';

import BadgesContainer from '../../../../../containers/badges/Badges.container';
import TribeCommentContainer from '../../../../../containers/programme/tribe/tribe-comment/TribeComment.container';
import TribeFilesContainer from '../../../../../containers/programme/tribe/tribe-files/TribeFiles.container';
import TribeInputContainer from '../../../../../containers/programme/tribe/tribe-input/TribeInput.container';

import { ReactComponent as LikeIcon } from '../../../../../resources/icons/like.svg';

import './tribe-comment.scss';

const timeSince = (date: string | Date): string => {
  const minuteInSeconds = 60;
  const hourInSeconds = minuteInSeconds * 60;
  const dayInSeconds = hourInSeconds * 24;
  const weekInSeconds = dayInSeconds * 7;

  const seconds = Math.floor(new Date().getTime() - new Date(date).getTime()) / 1000;

  let value = seconds / weekInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'week' : 'weeks'} ago`;
  }

  value = seconds / dayInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'day' : 'days'} ago`;
  }

  value = seconds / hourInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'hour' : 'hours'} ago`;
  }

  value = seconds / minuteInSeconds;
  if (value > 1) {
    value = Math.floor(value);
    return `${value} ${value === 1 ? 'minute' : 'minutes'} ago`;
  }

  return 'A few seconds ago';
};

type Props = {
  status: string;
  comment: Comment;
  files: FileType[];
  liked: boolean;
  comments: Comment[];
  hashtags: HashTag[];
  showReplies: boolean;
  postExternalReference?: string;
  reply: boolean;
  approval: boolean;
  createReaction: (value: string) => void;
  deleteReaction: (value: string) => void;
  refreshPost?: (value: string) => void;
  updateCommentStatus: (value: string, callbackFn: () => void) => void;
  evaluateSupportConversation: (value: string) => void;
  hierarchy: boolean;
  navigateToProfile: (value: string) => void;
};

const TribeComment = ({
  status,
  comment,
  files = [],
  liked,
  comments = [],
  hashtags,
  showReplies,
  postExternalReference,
  reply,
  approval,
  createReaction,
  deleteReaction,
  refreshPost,
  updateCommentStatus,
  evaluateSupportConversation,
  hierarchy,
  navigateToProfile
}: Props) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(comment.status ?? '');

  const { text, startDateTime, userName, reactionCount, awaitingApproval, systemUserExternalReference } = comment;

  const totalLikes = reactionCount?.find((reaction) => reaction.text === 'like')?.count || 0;

  const handleLikeClick = () => {
    if (status === ARCHIVED || approval) {
      return;
    }

    if (liked) {
      deleteReaction('like');
    } else {
      createReaction('like');
    }
  };

  const handleApprovalButtonClick = (status: string) => {
    updateCommentStatus(status, () => setApprovalStatus(status));
  };

  return (
    <div className={getClassNames('tribe-comment', { reply })} data-testid="tribe-comment">
      <div>
        <ProfilePicture src={comment.profilePictureExternalReference} alt={userName} small />
        <div>
          <div className="tribe-comment__bubble-container">
            <div
              className={getClassNames('tribe-comment__bubble', {
                approval: approval && approvalStatus === PENDING
              })}
            >
              <div className="tribe-comment__bubble__header">
                <button className="tribe-comment__bubble__name" onClick={() => navigateToProfile(userName as string)}>
                  {formatUsername(userName)}
                </button>
                <BadgesContainer
                  memberReference={comment.memberExternalReference}
                  badgeReferences={comment?.memberBadgeIconExternalReferences ?? []}
                />
              </div>
              <p className="tribe-comment__bubble__text">{text}</p>
              <TribeThumbnails files={files} comment />
              <TribeFilesContainer files={files} comment />
            </div>
          </div>

          <div className={getClassNames('tribe-comment__actions', { disabled: status === ARCHIVED })}>
            <p>{timeSince(startDateTime ?? '')}</p>
            {!approval && status !== ARCHIVED && <div className="tribe-comment__actions__seperator" />}
            {status !== ARCHIVED && !approval && (
              <p onClick={() => setShowReplyInput((prevState) => !prevState)}>Reply</p>
            )}
            {!awaitingApproval && <div className="tribe-comment__actions__seperator" />}
            {!awaitingApproval && (
              <div className="tribe-comment__actions__icon-container">
                <LikeIcon
                  className={getClassNames('tribe-comment__actions__icon-container__icon', {
                    liked,
                    disabled: status === ARCHIVED
                  })}
                  onClick={handleLikeClick}
                  data-testid="tribe-comment-like-icon"
                />
                <p>
                  {totalLikes} {totalLikes === 1 ? 'Like' : 'Likes'}
                </p>
              </div>
            )}
          </div>
          <div className="tribe-comment__approval-actions">
            {awaitingApproval && approvalStatus === PENDING && (
              <TribeApprovalButton text="APPROVE" onClick={() => handleApprovalButtonClick(APPROVED)} />
            )}
            {awaitingApproval && approvalStatus === PENDING && (
              <TribeApprovalButton text="DECLINE" onClick={() => handleApprovalButtonClick(DECLINED)} />
            )}
            {awaitingApproval && approvalStatus === APPROVED && (
              <TribeApprovalButton text="APPROVED" onClick={() => handleApprovalButtonClick(PENDING)} approved />
            )}
            {awaitingApproval && approvalStatus === DECLINED && (
              <TribeApprovalButton
                text="CONTACT"
                onClick={() => evaluateSupportConversation(systemUserExternalReference as string)}
              />
            )}
            {awaitingApproval && approvalStatus === DECLINED && (
              <TribeApprovalButton text="DECLINED" onClick={() => handleApprovalButtonClick(PENDING)} declined />
            )}
          </div>
        </div>
      </div>
      {showReplyInput && (
        <TribeInputContainer
          placeholder="Reply..."
          hashtags={hashtags}
          parent={comment}
          onPostSuccess={() => {
            setShowReplyInput(false);
            refreshPost?.(postExternalReference as string);
          }}
          comment
          replyingToComment
        />
      )}
      {showReplies &&
        comments.map((reply) => (
          <TribeCommentContainer
            key={reply.externalReference}
            status={status}
            comment={reply}
            hashtags={hashtags}
            showReplies={showReplies}
            postExternalReference={postExternalReference}
            refreshPost={refreshPost}
            approval={approval}
            reply
            hierarchy={hierarchy}
          />
        ))}
    </div>
  );
};

export default TribeComment;
